import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  assignPolicy,
  clearPolicyAssignment,
  createPolicy,
  deletePolicy,
  dryRunPolicy,
  fetchPolicies,
  fetchPolicyAssignments,
  type UpsertPolicyInput,
  updatePolicy
} from '@/api/policies.ts'

/**
 * Query hooks for the policy plane.
 *
 * Every write invalidates BOTH the profile list and the assignment table, because the two
 * are views of one fact: `assignedAgents` on a profile is the count the assignment rows
 * carry, and a saved edit that left the table showing the old profile name would misreport
 * the blast radius of the next edit (AGM-105's whole purpose).
 *
 * The dry run is a mutation rather than a query even though it writes nothing (AGM-106): it
 * is keyed to a draft the administrator is holding in a form, not to a URL, and caching it
 * under a key would serve a preview of an edit they have since changed.
 */

export const policyKeys = {
  all: ['admin', 'policies'] as const,
  list: ['admin', 'policies', 'list'] as const,
  assignments: ['admin', 'policies', 'assignments'] as const
}

export const usePolicies = (enabled: boolean) =>
  useQuery({ queryKey: policyKeys.list, queryFn: fetchPolicies, enabled, staleTime: 30_000, retry: false })

export const usePolicyAssignments = (enabled: boolean) =>
  useQuery({
    queryKey: policyKeys.assignments,
    queryFn: fetchPolicyAssignments,
    enabled,
    staleTime: 30_000,
    retry: false
  })

export const useCreatePolicy = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: UpsertPolicyInput) => createPolicy(input),
    onSuccess: () => client.invalidateQueries({ queryKey: policyKeys.all })
  })
}

export const useUpdatePolicy = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ policyId, version, input }: { policyId: string; version: number; input: UpsertPolicyInput }) =>
      updatePolicy(policyId, version, input),
    onSuccess: () => client.invalidateQueries({ queryKey: policyKeys.all })
  })
}

export const useDeletePolicy = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (policyId: string) => deletePolicy(policyId),
    onSuccess: () => client.invalidateQueries({ queryKey: policyKeys.all })
  })
}

export const useDryRunPolicy = () =>
  useMutation({
    mutationFn: ({ policyId, input }: { policyId: string; input: UpsertPolicyInput }) => dryRunPolicy(policyId, input)
  })

export const useAssignPolicy = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, policyId }: { agentId: string; policyId: string | null }) =>
      policyId === null ? clearPolicyAssignment(agentId) : assignPolicy(agentId, policyId),
    onSuccess: () => client.invalidateQueries({ queryKey: policyKeys.all })
  })
}
